<template>
    <ModalWrapper id="fare-rules-modal" v-model="isOpen" max-width="700px">
        <template v-slot:activator>
            <v-btn variant="text" size="small" color="info" v-bind="props" @click.stop="isOpen = true">
                {{ $t('flights.fare_rules') }}
            </v-btn>
        </template>
        <template v-slot:title>
            {{ $t('flights.fare_rules') }}
        </template>
        <template v-slot:content>
            <div v-if="rules">
                <v-row dense no-gutters>
                    <v-list density="compact" class="fare-rules__list d-flex flex-wrap text-primary w-100">
                        <v-list-item
                            v-for="rule in rules"
                            :key="rule.name"
                            variant="plain"
                            class="fare-rules__list-item pa-0"
                            link
                            slim
                            @click="onItemClick(getIdByname(rule.name))"
                        >
                            <v-list-item-title class="fare-rules__list-item-title">{{ rule.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-row>
                <div v-for="rule in rules" :id="getIdByname(rule.name)" :key="rule.name" class="mt-10 mb-4">
                    <div class="text-subtitle-1 text-black">
                        {{ rule.name }}
                    </div>
                    <div v-html="rule.text" />
                </div>
            </div>
            <div v-else style="position: relative; min-height: 100px">
                <flight-loader :is-loading="loading" />
                <div v-if="!loading">
                    {{ $t('empty_fare_rules_message') }}
                </div>
            </div>
        </template>
    </ModalWrapper>
</template>

<script setup lang="ts">
    import FlightLoader from '@/components/flights/snippets/FlightLoader.vue'

    const props = defineProps<{
        offerKey?: string
        processId?: string
    }>()
    const isOpen = ref(false)
    const loading = ref(false)
    const rules = ref<any[] | null>(null)
    const {$api} = useNuxtApp()
    const {t} = useI18n()
    const goTo = useGoTo()

    watch(isOpen, open => {
        if (open && !rules.value) {
            getFareRules()
        }
    })

    const getFareRules = async () => {
        try {
            loading.value = true
            const rq: Record<string, any> = {}

            if (props.offerKey) rq.offerKey = props.offerKey
            if (props.processId) rq.processId = props.processId

            const response = await $api.flightFares.get(rq)

            if (response.flightRules[0].paragraphs) {
                rules.value = response.flightRules[0].paragraphs
            } else if (response.flightRules[0].brandedFareName && response.flightRules[0].brandedFareDescription) {
                rules.value = [
                    {
                        name: response.flightRules[0].brandedFareName,
                        text: response.flightRules[0].brandedFareDescription,
                    },
                ]
            }
        } catch (e: any) {
            if (e.status === 400) EventBus.emit('OFFER_EXPIRED_EVENT')
            rules.value = [
                {
                    name: t('flights.no_rules'),
                    text: '',
                },
            ]
        } finally {
            loading.value = false
        }
    }

    const getIdByname = (name: string) => {
        return name.toLowerCase().replace(/\s/g, '')
    }

    const onItemClick = (id: string) => {
        goTo(document.getElementById(id), {
            container: '#fare-rules-modal .v-card',
        })
    }
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';
    .fare-rules {
        &__list {
            row-gap: 16px;
            column-gap: 16px;
        }
        &__list-item {
            flex: 0 1 calc(100% / 3 - 12px);
            min-height: unset;
        }
        &__list-item-title {
            white-space: normal;
            text-decoration: underline;
        }
    }
    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .fare-rules {
            &__list-item {
                flex: 0 1 calc(50% - 8px);
            }
        }
    }
</style>
